import { useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'

import SVGImage from '~/components/Image/SVGImage'

import { Breakpoint, useCheckBreakpoint } from '~/utils'

import { isDisplayOnlyCurriculumChange } from '../constants/usCurriculumStates.const'
import { getCurriculumFlagSrcKey } from '../utils/curriculumFlag.utils'

import CurriculumUnsetFlag from './CurriculumUnsetFlag'

interface Props {
  curriculumName: string
  curriculumCountryCode: string
  isDark?: boolean
  isFooter?: boolean
  isMenu?: boolean
  isCurrentCurriculum?: boolean
  /** No state yet: show the globe, since no flag would be truthful. */
  isUnset?: boolean
  currentCurriculumCode?: string
  onClick?: (curriculumName: string) => void
  isOnboarding?: boolean
  textClassName?: string
  rowClassName?: string
}

export default function CurriculumDropdownItem({
  isOnboarding,
  curriculumName,
  curriculumCountryCode,
  isDark,
  isFooter,
  isMenu,
  isCurrentCurriculum,
  isUnset,
  currentCurriculumCode,
  onClick,
  textClassName,
  rowClassName,
}: Props): React.ReactElement {
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.MD })
  const isMobileMenu = isMobile && isMenu
  const isDarkMenu = isDark && isMenu
  const curriculumCountryFlagImageSrc = getCurriculumFlagSrcKey(curriculumCountryCode, isFooter || isDarkMenu)
  const [isBold, setIsBold] = useState(false)
  const shouldBold = (isCurrentCurriculum && isFooter) || isBold

  // Reset bold state when curriculum changes or component unmounts
  useEffect(() => {
    // Reset bold state after a short delay to allow the bold effect to be visible
    const timer = setTimeout(() => {
      setIsBold(false)
    }, 2000) // Reset after 2 seconds

    return () => clearTimeout(timer)
  }, [curriculumName, isCurrentCurriculum])

  const { t } = useTranslation('generics')
  // Option rows (not the collapsed trigger) get the dark-menu row chrome
  const isDarkMenuOption = isDarkMenu && (Boolean(onClick) || Boolean(isCurrentCurriculum))
  return (
    <button
      aria-selected={!!isCurrentCurriculum}
      className={`flex items-center space-x-2.5 ${
        rowClassName ??
        (isDarkMenuOption
          ? // The selected row is marked by its orange label (see Figma), not a filled background
            'w-full px-3.5 py-3 duration-300 hover:bg-orange-faint'
          : '')
      }`}
      disabled={isCurrentCurriculum}
      onClick={() => {
        if (isFooter && isDisplayOnlyCurriculumChange(curriculumName, currentCurriculumCode)) {
          setIsBold(true)
        }
        onClick?.(curriculumName)
      }}
      role="option"
      type="button"
    >
      {isUnset ? (
        <CurriculumUnsetFlag className={isOnboarding ? 'w-8' : 'w-6'} />
      ) : (
        <SVGImage
          alt={t('countryCode')}
          className={`${isOnboarding ? 'w-8' : 'w-6'}`}
          imageClassName="h-6 rounded-0.5"
          srcKey={curriculumCountryFlagImageSrc}
        />
      )}
      <span
        className={`${
          textClassName ??
          (isOnboarding
            ? 'text-5 font-medium'
            : isFooter
            ? 'text-xs'
            : isMobileMenu
            ? 'pl-2.5 tracking-btn-wide'
            : 'text-3')
        } ${shouldBold ? 'font-bold' : ''} text-start`}
      >
        {curriculumName}
      </span>
    </button>
  )
}
